import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Search,
  Shield,
  Ban,
  MapPin,
  Monitor,
  Smartphone,
  AlertTriangle,
  Trash2,
  Fingerprint as FpIcon,
} from "lucide-react";

/**
 * Admin Security cockpit — `/admin/security`.
 *
 * Two tabs:
 *   1. Lookup → search user → show their device sessions, IPs, geo,
 *      and linked accounts (sockpuppets). Per-row ban actions.
 *   2. Bans → list active IP & fingerprint bans, lift any.
 */

function ShortHash({ value }) {
  if (!value) return <span className="text-zinc-400">—</span>;
  return (
    <code className="text-[11px] bg-zinc-100 px-1.5 py-0.5 rounded font-mono">
      {value.slice(0, 10)}…{value.slice(-4)}
    </code>
  );
}

function PillBan({ children, onClick, kind = "ip" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold border transition-colors ${
        kind === "ip"
          ? "border-rose-300 text-rose-700 hover:bg-rose-50"
          : "border-orange-300 text-orange-700 hover:bg-orange-50"
      }`}
      data-testid={`ban-${kind}-btn`}
    >
      <Ban size={11} /> {children}
    </button>
  );
}

function LookupTab({ inspectTarget }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced search
  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/security/lookup?q=${encodeURIComponent(q)}`);
        setResults(r.data.items || []);
      } catch (_e) { /* admin-only — silent on transient errors */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const loadUser = async (u) => {
    setSelectedUser(u);
    setResults([]);
    setQ("");
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${u.user_id}/devices`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load devices");
    } finally {
      setLoading(false);
    }
  };

  // External trigger: Risk Queue → Inspect propagates the user through.
  useEffect(() => {
    if (inspectTarget && inspectTarget.user_id && inspectTarget.user_id !== selectedUser?.user_id) {
      loadUser(inspectTarget);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectTarget]);

  const banValue = async (kind, value, reason) => {
    if (!value) return;
    const confirmMsg = `Ban this ${kind}?\n\n${value}\n\nReason: ${reason || "(none)"}`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post("/admin/bans", { kind, value, reason });
      toast.success(`${kind === "ip" ? "IP" : "Device"} banned`);
      if (selectedUser) await loadUser(selectedUser);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ban failed");
    }
  };

  return (
    <div>
      <div className="relative" data-testid="security-search-wrap">
        <Search size={16} className="absolute left-3 top-2.5 text-zinc-400" />
        <input
          type="search"
          placeholder="Search by email, username, name, or IP…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-xl border border-zinc-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
          data-testid="security-search-input"
        />
        {results.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-zinc-200 rounded-xl shadow-lg overflow-hidden">
            {results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => loadUser(u)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center justify-between gap-2"
                data-testid={`security-search-row-${u.user_id}`}
              >
                <span>
                  <b>{u.username || u.email}</b>{" "}
                  <span className="text-zinc-500">— {u.email}</span>
                </span>
                {u.banned_at && (
                  <span className="text-[10px] font-bold uppercase text-rose-600">Banned</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="mt-6 text-sm text-zinc-500 animate-pulse">Loading device map…</div>
      )}

      {data && !loading && (
        <div className="mt-6 space-y-6">
          {/* User header card */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="security-user-header">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold">User</p>
                <h2 className="font-display font-black text-xl">
                  {data.user.username || data.user.email}
                  {data.user.banned_at && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
                      <Ban size={10} /> Banned
                    </span>
                  )}
                </h2>
                <p className="text-sm text-zinc-600">{data.user.email}</p>
              </div>
              <div className="text-right text-sm">
                <div className="text-zinc-600">
                  <b className="text-zinc-900">{data.totals.unique_ips}</b> IPs ·{" "}
                  <b className="text-zinc-900">{data.totals.unique_fps}</b> devices ·{" "}
                  <b className={data.totals.linked_accounts > 0 ? "text-rose-700" : "text-zinc-900"}>
                    {data.totals.linked_accounts}
                  </b>{" "}
                  linked accounts
                </div>
              </div>
            </div>

            {/* Signup / verification IPs */}
            <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
              {data.user.signup_ip && (
                <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2">
                  <div className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-0.5">Signup IP</div>
                  <code className="text-sm font-mono">{data.user.signup_ip}</code>
                  {data.user.signup_ip_geo && (
                    <div className="text-zinc-600 mt-0.5">
                      {data.user.signup_ip_geo.city}, {data.user.signup_ip_geo.country} · {data.user.signup_ip_geo.isp}
                    </div>
                  )}
                </div>
              )}
              {data.user.verification_ip && (
                <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2">
                  <div className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-0.5">Verification IP</div>
                  <code className="text-sm font-mono">{data.user.verification_ip}</code>
                  {data.user.verification_ip_geo && (
                    <div className="text-zinc-600 mt-0.5">
                      {data.user.verification_ip_geo.city}, {data.user.verification_ip_geo.country} · {data.user.verification_ip_geo.isp}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sessions table */}
          <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="security-sessions-card">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="font-bold text-sm">Device & IP sessions</h3>
              <span className="text-xs text-zinc-500">{data.sessions.length} most recent</span>
            </div>
            {data.sessions.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">
                No sessions yet — user hasn't logged in since signal capture was wired up.
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {data.sessions.map((s) => {
                  const Icon = s.ua_family === "iOS" || s.ua_family === "Android" ? Smartphone : Monitor;
                  return (
                    <div
                      key={`${s.ip}-${s.device_fp || "nofp"}`}
                      className="px-4 py-3 flex items-start gap-3 flex-wrap"
                      data-testid={`security-session-${s.id}`}
                    >
                      <Icon size={18} className="text-zinc-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-[260px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-sm font-mono font-bold">{s.ip || "—"}</code>
                          {s.ip_geo?.country && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-zinc-600">
                              <MapPin size={11} /> {s.ip_geo.city}, {s.ip_geo.country}
                            </span>
                          )}
                          <span className="text-[11px] text-zinc-500">{s.ua_family}</span>
                          <span className="text-[11px] text-zinc-400">×{s.hit_count}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>
                            <FpIcon size={10} className="inline-block" /> <ShortHash value={s.device_fp} />
                          </span>
                          {s.ip_geo?.isp && <span>· {s.ip_geo.isp}</span>}
                          <span>· last seen {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : "—"}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {s.ip && (
                          <PillBan kind="ip" onClick={() => banValue("ip", s.ip, `Linked to ${data.user.email}`)}>
                            Ban IP
                          </PillBan>
                        )}
                        {s.device_fp && (
                          <PillBan kind="fingerprint" onClick={() => banValue("fingerprint", s.device_fp, `Linked to ${data.user.email}`)}>
                            Ban device
                          </PillBan>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Linked accounts */}
          {data.linked_accounts.length > 0 && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 overflow-hidden" data-testid="security-linked-card">
              <div className="px-4 py-3 border-b border-rose-200 flex items-center gap-2">
                <AlertTriangle size={16} className="text-rose-600" />
                <h3 className="font-bold text-sm text-rose-900">
                  {data.linked_accounts.length} other account{data.linked_accounts.length === 1 ? "" : "s"} share an IP or device with this user
                </h3>
              </div>
              <div className="divide-y divide-rose-100">
                {data.linked_accounts.map((a) => (
                  <div
                    key={a.user_id}
                    className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                    data-testid={`security-linked-${a.user_id}`}
                  >
                    <div className="text-sm">
                      <b>{a.username || a.email}</b>{" "}
                      <span className="text-zinc-600">— {a.email}</span>
                      {a.banned_at && (
                        <span className="ml-2 text-[10px] font-bold uppercase text-rose-700">Banned</span>
                      )}
                      <div className="text-[11px] text-zinc-600 mt-0.5">
                        Matched via {a.match_via.join(" + ")} · last seen {new Date(a.last_seen_at).toLocaleString()}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => loadUser({ user_id: a.user_id, email: a.email, username: a.username })}
                      className="text-xs font-bold rounded-full bg-white border border-zinc-200 px-3 py-1 hover:bg-zinc-100"
                      data-testid={`security-linked-open-${a.user_id}`}
                    >
                      Inspect →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!data && !loading && !selectedUser && (
        <div className="mt-8 text-center text-sm text-zinc-500 border-2 border-dashed border-zinc-200 rounded-2xl p-10">
          <Shield className="mx-auto mb-2 text-zinc-400" size={24} />
          Search any user above to see every IP and device they've used,
          plus other accounts that share an IP or device fingerprint.
        </div>
      )}
    </div>
  );
}

function BansTab() {
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ kind: "ip", value: "", reason: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bans");
      setBans(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load bans");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const lift = async (id) => {
    if (!window.confirm("Lift this ban?")) return;
    try {
      await api.delete(`/admin/bans/${id}`);
      toast.success("Ban lifted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lift failed");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.value.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/bans", form);
      toast.success("Ban created");
      setForm({ kind: "ip", value: "", reason: "" });
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Quick-add form */}
      <form
        onSubmit={add}
        className="rounded-2xl border border-zinc-200 bg-white p-4 grid sm:grid-cols-[120px_1fr_1fr_auto] gap-2"
        data-testid="ban-quick-add"
      >
        <select
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
          className="rounded-lg border border-zinc-200 px-2 py-2 text-sm bg-white"
          data-testid="ban-kind"
        >
          <option value="ip">IP address</option>
          <option value="fingerprint">Device FP</option>
        </select>
        <input
          type="text"
          required
          placeholder={form.kind === "ip" ? "192.0.2.42" : "Device fingerprint (64-char hex)"}
          value={form.value}
          onChange={(e) => setForm({ ...form, value: e.target.value })}
          className="rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono"
          data-testid="ban-value"
        />
        <input
          type="text"
          placeholder="Reason (optional)"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          className="rounded-lg border border-zinc-200 px-3 py-2 text-sm"
          data-testid="ban-reason"
        />
        <button
          type="submit"
          disabled={adding}
          className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 py-2 text-sm disabled:opacity-60"
          data-testid="ban-add-submit"
        >
          {adding ? "Banning…" : "Ban"}
        </button>
      </form>

      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h3 className="font-bold text-sm">Active bans</h3>
          <span className="text-xs text-zinc-500">{bans.length}</span>
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-zinc-500 animate-pulse">Loading…</div>
        ) : bans.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-500">
            No active bans. Calm seas.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {bans.map((b) => (
              <div
                key={b.id}
                className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                data-testid={`ban-row-${b.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${b.kind === "ip" ? "bg-rose-100 text-rose-700" : "bg-orange-100 text-orange-700"}`}>
                      {b.kind === "ip" ? <Ban size={10} /> : <FpIcon size={10} />}
                      {b.kind}
                    </span>
                    <code className="text-sm font-mono break-all">{b.value}</code>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    {b.reason && <span>{b.reason} · </span>}
                    Created {b.created_at ? new Date(b.created_at).toLocaleString() : "—"}
                    {b.created_by_email && <span> by {b.created_by_email}</span>}
                    {b.expires_at && <span> · expires {new Date(b.expires_at).toLocaleString()}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => lift(b.id)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 hover:bg-zinc-200 px-3 py-1.5 text-xs font-bold"
                  data-testid={`ban-lift-${b.id}`}
                >
                  <Trash2 size={11} /> Lift
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RiskQueueTab({ onInspect }) {
  const [items, setItems] = useState([]);
  const [scanned, setScanned] = useState(0);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(50);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/security/risk-queue?min_score=${minScore}&limit=50`);
      setItems(r.data.items || []);
      setScanned(r.data.scanned || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load risk queue");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minScore]);

  const clear = async (user_id, email) => {
    if (!window.confirm(`Mark ${email} as reviewed & cleared?\n\nThey'll drop out of the queue.`)) return;
    try {
      await api.post(`/admin/security/risk-queue/${user_id}/clear`, { note: "reviewed via cockpit" });
      toast.success("Cleared from queue");
      setItems((arr) => arr.filter((u) => u.user_id !== user_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const ban = async (user_id, email) => {
    if (!window.confirm(`Ban account ${email}?`)) return;
    try {
      await api.post(`/admin/users/${user_id}/ban`, { reason: "flagged by risk queue" });
      toast.success("Account banned");
      setItems((arr) => arr.filter((u) => u.user_id !== user_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ban failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="risk-queue-tab">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-amber-900">
          <b>Risk queue</b> auto-flags accounts scoring ≥
          <select
            value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value, 10))}
            className="mx-1 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-xs font-bold"
            data-testid="risk-min-score"
          >
            {[40, 50, 60, 70, 80].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          on the combined fraud-signal model (IP velocity, VPN/datacenter, disposable email, country mismatch, device sockpuppets).
        </div>
        <div className="text-xs text-amber-900/80 whitespace-nowrap">
          {loading ? "scanning…" : `${items.length} flagged · ${scanned} scanned`}
        </div>
      </div>

      {loading && (
        <div className="text-sm text-zinc-500 animate-pulse text-center py-8">Scanning the last 90 days…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center text-sm text-zinc-500 border-2 border-dashed border-zinc-200 rounded-2xl p-10">
          <Shield className="mx-auto mb-2 text-emerald-500" size={24} />
          Clean slate — no accounts above the threshold need review.
        </div>
      )}

      <div className="space-y-2">
        {items.map((u) => (
          <div
            key={u.user_id}
            className="rounded-2xl border border-zinc-200 bg-white p-4"
            data-testid={`risk-row-${u.user_id}`}
          >
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center justify-center min-w-[40px] rounded-full px-2 py-0.5 text-xs font-black ${
                      u.risk_score >= 70
                        ? "bg-rose-600 text-white"
                        : "bg-amber-500 text-white"
                    }`}
                    data-testid={`risk-score-${u.user_id}`}
                  >
                    {u.risk_score}
                  </span>
                  <b className="text-sm">{u.username || u.email}</b>
                  <span className="text-xs text-zinc-500">{u.email}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {u.account_type || u.role || "user"} · joined{" "}
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  {u.verification_status && ` · ${u.verification_status}`}
                </div>
                <ul className="mt-2 space-y-1">
                  {u.risk_signals.map((sig, i) => (
                    <li
                      key={`${u.user_id}-sig-${i}`}
                      className="flex items-center gap-2 text-xs"
                    >
                      <span
                        className={`inline-block h-1.5 w-1.5 rounded-full ${
                          sig.severity === "high"
                            ? "bg-rose-500"
                            : sig.severity === "medium"
                            ? "bg-amber-500"
                            : "bg-zinc-400"
                        }`}
                      />
                      <span className="text-zinc-700">{sig.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => onInspect(u)}
                  className="rounded-full bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-100 px-3 py-1.5 text-xs font-bold"
                  data-testid={`risk-inspect-${u.user_id}`}
                >
                  Inspect
                </button>
                <button
                  type="button"
                  onClick={() => clear(u.user_id, u.email)}
                  className="rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-3 py-1.5 text-xs font-bold"
                  data-testid={`risk-clear-${u.user_id}`}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => ban(u.user_id, u.email)}
                  className="rounded-full bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 text-xs font-bold"
                  data-testid={`risk-ban-${u.user_id}`}
                >
                  <Ban size={11} className="inline -mt-0.5" /> Ban
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminSecurity() {
  const [tab, setTab] = useState("lookup");
  const [inspectTarget, setInspectTarget] = useState(null);

  const handleInspect = (user) => {
    setInspectTarget({
      user_id: user.user_id,
      email: user.email,
      username: user.username,
    });
    setTab("lookup");
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-24" data-testid="admin-security-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-3"
          data-testid="security-back-admin"
        >
          <ArrowLeft size={14} /> Admin
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-2">
              <Shield size={24} /> Security cockpit
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              Device & IP forensics for every account. Ban bad actors by IP or device.
            </p>
          </div>
        </div>

        <div className="inline-flex bg-zinc-200/60 rounded-full p-1 mb-5">
          <button
            type="button"
            onClick={() => setTab("lookup")}
            className={`px-4 py-1.5 text-sm font-bold rounded-full ${tab === "lookup" ? "bg-white shadow text-zinc-900" : "text-zinc-600"}`}
            data-testid="security-tab-lookup"
          >
            User lookup
          </button>
          <button
            type="button"
            onClick={() => setTab("risk")}
            className={`px-4 py-1.5 text-sm font-bold rounded-full ${tab === "risk" ? "bg-white shadow text-zinc-900" : "text-zinc-600"}`}
            data-testid="security-tab-risk"
          >
            Risk queue
          </button>
          <button
            type="button"
            onClick={() => setTab("bans")}
            className={`px-4 py-1.5 text-sm font-bold rounded-full ${tab === "bans" ? "bg-white shadow text-zinc-900" : "text-zinc-600"}`}
            data-testid="security-tab-bans"
          >
            Bans
          </button>
        </div>

        {tab === "lookup" && <LookupTab inspectTarget={inspectTarget} />}
        {tab === "risk" && <RiskQueueTab onInspect={handleInspect} />}
        {tab === "bans" && <BansTab />}
      </div>
    </div>
  );
}
